/**
 * @namespace Trait
 *
 * @author based on https://gist.github.com/AmyAmy/68ca8c52ec6d92da6206201e4def97d4
 * @author based on https://gist.github.com/lukescott/36453a75c39c539f5c7d
 *
 * @description
 * Traits (aka mixins) are used to decorate an extended class with functionality from Trait-objects.
 *
 * #### Extending Substance Node-classes
 * When traits are used to extend a Node class, the extending class's `schema` needs to define certain properties to
 * be able to use the Trait's functions. See docs for each trait for these requirements.
 *
 * @example
 * const Trait1 = {
 * 	method1() {}
 * }
 *
 * const Trait2 = {
 * 	method2() {}
 * }
 *
 * const Trait3 = addTraits({
 * 	method3() {}
 * }, Trait2)
 *
 * hasTrait(Trait3, Trait2) // true
 * hasTrait(Trait3, Trait1) // false
 *
 * @example
 * class Base {}
 * class Foo extends withTraits(Base, Trait1) {}
 *
 * hasTrait(Foo, Trait1) // true
 * hasTrait(Foo, Trait2) // false
 *
 * @example
 * class Foo2 extends Foo {}
 *
 * hasTrait(Foo2, Trait1) // true
 * hasTrait(Foo2, Trait2) // false
 *
 * @example
 * class Foo3 extends withTraits(Foo, Trait2) {}
 *
 * hasTrait(Foo3, Trait1) // true
 * hasTrait(Foo3, Trait2) // true
 *
 * @example
 * class Bar extends withTraits(Base, Trait3) {}
 *
 * hasTrait(Bar, Trait1) // false
 * hasTrait(Bar, Trait2) // true
 * hasTrait(Bar, Trait3) // true
 *
 * @example
 * const Trait4 = {
 *	constructor() {
 *		this.prop = 4
 *	},
 *	method4() {
 *		console.log(this.prop)
 *	}
 * }
 * class Baz extends withTraits(Base, Trait4) {}
 * new Baz().method4() // 4
 */

/**
 * Add traits into an Object (another trait)
 *
 * @memberOf Trait
 * @param {object} object The object (can be another trait) to add traits to.
 * @param {Array.<object>} traits An array of traits to add.
 * @returns {object} The original object, now with the added traits.
 */
function addTraits(object, ...traits) {
    const t = []

    if (object.__traits__) {
        Array.prototype.push.apply(t, object.__traits__)
    }

    const props = {
        __traits__: {
            value: t,
            writable: true,
            configurable: true
        }
    }

    for(let trait of traits) {
        if (trait.__traits__) {
            Array.prototype.push.apply(t, trait.__traits__)
        }

        for(let name of Object.getOwnPropertyNames(trait)) {
            // ignore constructor and __traits__ properties
            if (['constructor', '__traits__'].includes(name) || object.hasOwnProperty(name)) {
                continue
            }

            props[name] = {
                value: trait[name],
                writable: true,
                configurable: true
            }
        }

        t.push(trait)
    }

    Object.defineProperties(object, props)
    return object
}

/**
 * Adds traits to a class.
 *
 * @memberOf Trait
 * @param {function} baseClass The class to add the traits to.
 * @param {Array.<object>} traits One or more traits to add to the class.
 * @returns {function} A class which extends baseClass, and has all the specified traits.
 *
 * @example class Foo extends withTraits(Base, Trait1) {}
 */
function withTraits(baseClass, ...traits) {
    class TraitedClass extends baseClass {
        constructor() {
            super(...arguments)

            // Run any inherited constructors
            traits.filter((trait) => trait.constructor)
                .forEach((trait) => trait.constructor.apply(this, arguments))
        }
    }

    addTraits.apply(this, [TraitedClass.prototype, ...traits]) // eslint-disable-line

    return TraitedClass
}

/**
 * Checks to see if a class or instance has a trait
 *
 * @memberOf Trait
 * @param {function|Object} object The class or instance to check.
 * @param {object} trait The trait to check.
 * @returns {Boolean} True if the object has the specified trait.
 */
function hasTrait(object, trait) {
    const traits = (typeof object === 'function') ? object.prototype.__traits__ : object.__traits__
    return Array.isArray(traits) && traits.includes(trait)
}

export {
    addTraits,
    withTraits,
    hasTrait
}
